import React, { memo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import dynamic from 'next/dynamic';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import { LoadingSpinner } from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import {
    creditAppGetPreQualifiedClick,
} from 'reaxl-analytics-handlers';

import offsetCalc from '@/utilities/offSetScrollCalculation';
import scrollToElement from '@/utilities/scrollToElement';

import { snapshotPaymentDetailsClick } from '@/analytics/vdpAnalyticsHandlers';

import {
    authDuck,
    dealsDuck,
    globalConfigsDuck,
    paymentsDuck,
    userDuck,
} from '@/ducks';

import {
    vdpActiveInteractionDuck,
    vdpResultsDuck,
} from '@/ducks/vdp';

import usePaymentData from '@/hooks/usePaymentData';

import DealBox from '@/components/DealBox/DealBox';
import DealTypeTabs from '@/components/DealTypeTabs/index.js';

const PricingCard = dynamic(() => import(
    /* webpackChunkName: "PricingCard" */
    '@/components/PricingCard/index.js'
), { ssr: true });

export function PricingSnapshotContainer({
    emailCTAProps,
    config,
}) {

    const {
        accelerate: [, {
            credit_app: enableSDPCreditApp,
        }],
        standalone_credit_app: [enableStandaloneCreditApp],
        nds_right_rail: [isNDSRightRailEnabled],
    } = useFeatures([
        'accelerate',
        'standalone_credit_app',
        'nds_right_rail',
    ]);

    const { supportedDealTypes } = config;
    const dispatch = useDispatch();
    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory) || {};
    const isLoggedIn = useSelector(authDuck.selectors.isLoggedIn);
    const { creditApp, id, owner = {}, pricingDetail = {}, paymentServices, vin, zip } = listing;
    const { privateSeller: isPrivateSeller, hideEmailForm, location = {} } = owner;

    const creditAppHost = useSelector(globalConfigsDuck.selectors.getCreditAppHost);
    const paymentsInfo = useSelector(paymentsDuck?.selectors?.getPaymentsInfo) || {};
    const {
        zip: paymentsZip,
        interactions: {
            hasRequestedTaxesFees,
            isNationalRates,
        } = {},
    } = paymentsInfo || {};
    const userZip = useSelector(userDuck.selectors.getZip) || null;
    const pencilingPayment = useSelector((state) => paymentsDuck?.selectors?.getLowestPencilingPaymentByListingId(state, id));
    const leasePaymentResponse = useSelector((state) => paymentsDuck?.selectors?.getLowestLeasePaymentByListingId(state, id));
    const paymentEmphasis = useSelector((state) => paymentsDuck?.selectors?.getMyWalletInteraction(state, 'paymentEmphasis'));
    const paymentOption = useSelector(paymentsDuck?.selectors?.getPaymentOption) || 'finance';
    const existingDeal = useSelector((state) => dealsDuck.selectors.getDealByListingVin(state, vin)) || {};
    const [, , paymentsData] = usePaymentData(supportedDealTypes);

    const { id: existingDealId } = existingDeal;
    const isExistingDeal = !!existingDealId;

    const device = useDevice();
    const suppressPriceCardCTA = device?.is?.lg && isNDSRightRailEnabled;

    const scrollToNativeDealSection = () => {
        const offset = offsetCalc(25, 'sticky-header-vdp');
        const isNDSRendered = document.getElementById('nds');
        const destination = isNDSRendered ? 'nds' : 'nativeDealContainer';
        scrollToElement(destination, offset);
    };

    // Handles click on monthly payment block, if enabled
    const handleWalletPricingDetailsClick = (event) => {
        event.stopPropagation();

        scrollToNativeDealSection();
        sendClick(snapshotPaymentDetailsClick, event, {});
    };

    const handleThirdPartyCreditAppCTAClick = (event) => {
        event.stopPropagation();
        sendClick(creditAppGetPreQualifiedClick, event, { listingId: id, partner: creditApp?.partner, section: 'price-card' });
    };

    const handleSACACreditAppClick = (event) => {
        event.stopPropagation();
        sendClick(creditAppGetPreQualifiedClick, event, { listingId: id, partner: 'saca', section: 'price-card' });
        dispatch(vdpActiveInteractionDuck.creators.setKeys({ showFinanceAppModal: true }));
    };

    const hideEmailCTA = isPrivateSeller || hideEmailForm;
    const isDRAccelerate = paymentServices?.digitalRetailingType === 'accelerate';

    const hasTaxesFeesValue = (paymentsData || []).some((paymentOffer) => paymentOffer?.paymentInfo?.taxesAndFees > 0);
    const includeTaxesFees = hasRequestedTaxesFees && !isNationalRates && hasTaxesFeesValue;

    const isOnlyFinanceDeal = supportedDealTypes?.length === 1 && supportedDealTypes[0] === 'finance';

    const paymentCardCTAProps = {
        dealsDuck,
        enableSDPCreditApp: enableSDPCreditApp && isDRAccelerate,
        enableSACAPriceCard: enableStandaloneCreditApp && !isDRAccelerate && !!creditAppHost && !!owner?.sacaMmdId,
        handleSACACreditAppClick,
        listing,
        paymentsDuck,
        hideEmailCTA,
        listedPrice: pricingDetail.salePrice || pricingDetail.msrp,
        user: {
            zip: paymentsZip || userZip || zip || location?.address?.zip || null,
        },
        pencilingPayment,
        leasePaymentResponse,
        paymentEmphasis,
        existingDeal,
        isLoggedIn,
        paymentOption,
        paymentsInfo,
        userDuck,
    };

    return (
        <>
            <PricingCard
                includeTaxesFees={includeTaxesFees}
                handlePriceAndPaymentsClick={handleWalletPricingDetailsClick}
                includeFinancePaymentOfferToWalletCTA={isOnlyFinanceDeal}
            />
            <>
                {supportedDealTypes?.length > 1 && !isExistingDeal && (
                    <DealTypeTabs
                        includeTaxesFees={includeTaxesFees}
                        supportedDealTypes={supportedDealTypes}
                    />
                )}
                {!supportedDealTypes && (
                    <LoadingSpinner
                        className="margin-top-0"
                        height={95}
                    />
                )}
                <DealBox
                    displayPaymentError={!isOnlyFinanceDeal}
                    onCreditAppCTAClick={handleThirdPartyCreditAppCTAClick}
                    paymentCardCTAProps={paymentCardCTAProps}
                    suppressPriceCardCTA={suppressPriceCardCTA}
                    emailCTAProps={emailCTAProps}
                    supportedDealTypes={supportedDealTypes}
                />
            </>
        </>
    );
}

export default memo(PricingSnapshotContainer);
